import { buildArtifactFlowPath, buildSafetyReportArtifactPath } from './reportFlow';

export const ACCEPTANCE_HIERARCHY_COPY =
  'Risk Patterns are reviewed into Safety Cases. Safety Cases produce Safety Scans. Signed Safety Scans are frozen. Safety Control Workspace is the portfolio hub.';

export const ACCEPTANCE_UI_LABELS = {
  openLiveSafetyCase: 'Open Safety Case',
  viewSignedSafetyScan: 'View Signed Safety Scan',
  viewSignedScan: 'View Signed Scan',
  viewLatestSignedScan: 'View Latest Signed Scan',
  openDraftSafetyScan: 'Open Draft Safety Scan',
  continueSafetyScan: 'Continue Safety Scan',
  createUpdatedSafetyScan: 'Create Updated Safety Scan',
  downloadEvidencePackage: 'Download Package',
  downloadPackage: 'Download Package',
  downloadCustomerPdf: 'Download Customer PDF',
  viewScanArchive: 'Scan archive',
  viewRevisions: 'Revisions',
  reviewFollowUpActivity: 'Review New Activity',
  reviewFollowUpQueue: 'Review New Activity',
  reviewNewActivity: 'Review New Activity',
  viewLiveCases: 'View Safety Cases',
  reviewRiskPattern: 'Review Risk Pattern',
} as const;

function normalizeText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function resolveSafetyCaseId(input?: {
  finding_id?: string | null;
  safety_case_id?: string | null;
} | null): string {
  return String(input?.safety_case_id || input?.finding_id || '').trim();
}

export function resolveSignedSafetyScanReportId(input?: {
  latest_finalized_report_id?: string | null;
  report_id?: string | null;
  report_status?: string | null;
} | null): string {
  if (!input || normalizeText(input.report_status) !== 'finalized') return '';
  return String(input.latest_finalized_report_id || input.report_id || '').trim();
}

export function resolveDraftSafetyScanReportId(input?: {
  active_draft_report_id?: string | null;
} | null): string {
  return String(input?.active_draft_report_id || '').trim();
}

export function buildLiveSafetyCasePath(args: {
  caseId: string;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  claimReadinessFocus?: string | null;
}): string {
  const caseId = String(args.caseId || '').trim();
  const query = new URLSearchParams();
  if (args.orgId) query.set('org_id', String(args.orgId));
  if (args.siteId) query.set('site_id', String(args.siteId));
  if (args.workflowId) query.set('workflow_id', String(args.workflowId));
  if (args.incidentId) query.set('incident_id', String(args.incidentId));
  const claimReadinessFocus = String(args.claimReadinessFocus || '').trim();
  if (claimReadinessFocus) query.set('claim_readiness_focus', claimReadinessFocus);
  query.set('case_id', caseId);
  const suffix = query.toString();
  return `/acceptance/cases/${encodeURIComponent(caseId)}${suffix ? `?${suffix}` : ''}`;
}

export function buildSignedSafetyScanFallbackPath(row: {
  latest_finalized_report_id?: string | null;
  report_id?: string | null;
  report_status?: string | null;
  site_id?: string | null;
  scope?: { site_id?: string | null; zones?: unknown[]; zone_id?: string | null; risk_type?: string | null } | null;
  device_id?: string | null;
  zone_id?: string | null;
  camera_id?: string | null;
  risk_type?: string | null;
  risk_label?: string | null;
  representative_incident_id?: string | null;
  workflow_id?: string | null;
}, scope: { orgId?: string | null; siteId?: string | null }): string {
  const reportId = resolveSignedSafetyScanReportId(row);
  const siteId = String(row.site_id || row.scope?.site_id || scope.siteId || '').trim() || undefined;
  const scopeZones = row.scope?.zones;
  const zones = Array.isArray(scopeZones) ? scopeZones : [];
  return buildSafetyReportArtifactPath({
    reportId,
    orgId: scope.orgId,
    siteId,
    workflowId: row.workflow_id || undefined,
    deviceId: row.device_id || undefined,
    zoneId: row.zone_id || String(zones[0] || row.scope?.zone_id || '').trim() || undefined,
    cameraId: row.camera_id || undefined,
    reportFrom: undefined,
    reportTo: undefined,
    caseId: row.representative_incident_id || undefined,
    incidentId: row.representative_incident_id || undefined,
    policyType: row.risk_type || row.scope?.risk_type || undefined,
    policyName: row.risk_label || undefined,
  });
}

export function buildDraftSafetyScanFallbackPath(
  row: {
    site_id?: string | null;
    device_id?: string | null;
    zone_id?: string | null;
    camera_id?: string | null;
    risk_type?: string | null;
    risk_label?: string | null;
    representative_incident_id?: string | null;
    workflow_id?: string | null;
    finding_id?: string | null;
    safety_case_id?: string | null;
  },
  scope: { orgId?: string | null; siteId?: string | null },
  searchParams?: URLSearchParams | null,
): string {
  const caseId = resolveSafetyCaseId(row) || row.representative_incident_id || undefined;
  return buildArtifactFlowPath({
    kind: 'proof_pack',
    task: 'investigate_incident',
    orgId: scope.orgId,
    siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
    deviceId: row.device_id || undefined,
    zoneId: row.zone_id || undefined,
    cameraId: row.camera_id || undefined,
    reportFrom: searchParams?.get('report_from') || undefined,
    reportTo: searchParams?.get('report_to') || undefined,
    caseId,
    incidentId: row.representative_incident_id || undefined,
    policyType: row.risk_type || undefined,
    policyName: row.risk_label || undefined,
  });
}

export function resolveDraftSafetyScanButtonLabel(input?: {
  active_draft_report_id?: string | null;
  report_status?: string | null;
} | null): typeof ACCEPTANCE_UI_LABELS.continueSafetyScan | typeof ACCEPTANCE_UI_LABELS.openDraftSafetyScan {
  if (resolveDraftSafetyScanReportId(input)) {
    return normalizeText(input?.report_status) === 'draft'
      ? ACCEPTANCE_UI_LABELS.continueSafetyScan
      : ACCEPTANCE_UI_LABELS.openDraftSafetyScan;
  }
  return ACCEPTANCE_UI_LABELS.openDraftSafetyScan;
}

export type PortfolioHierarchyAction = {
  key: string;
  label: string;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
};

export type PortfolioActionGroup = 'liveCase' | 'scanExport';

export type GroupedPortfolioAction = PortfolioHierarchyAction & {
  group: PortfolioActionGroup;
};

export type SnapshotBannerActionTier = 'primary' | 'secondary' | 'tertiary';

export type SnapshotBannerAction = PortfolioHierarchyAction & {
  tier: SnapshotBannerActionTier;
};

export function buildSnapshotBannerWorkflowActions(args: {
  row: {
    finding_id?: string | null;
    safety_case_id?: string | null;
    latest_finalized_report_id?: string | null;
    report_id?: string | null;
    report_status?: string | null;
    followup_required?: boolean | null;
    has_new_activity_since_report?: boolean | null;
    workflow_changed_since_signoff?: boolean | null;
    active_draft_report_id?: string | null;
    review_status?: string | null;
  };
  onReviewFollowUp: () => void;
  onOpenLiveCase: () => void;
  onCreateUpdatedScan: () => void;
  onViewSignedScan: () => void;
  onOpenDraftScan: () => void;
  onOpenReview: () => void;
  onSaveFinding: () => void;
  resolvePrimaryBlocker: () => { code: string };
}): SnapshotBannerAction[] {
  const row = args.row;
  const actions: SnapshotBannerAction[] = [];
  const pushUnique = (action: SnapshotBannerAction) => {
    if (actions.some((existing) => existing.label === action.label)) return;
    actions.push(action);
  };
  const isFinalizedFollowUpSnapshot =
    normalizeText(row.report_status) === 'finalized'
    && Boolean(row.followup_required || row.has_new_activity_since_report || row.workflow_changed_since_signoff);

  if (isFinalizedFollowUpSnapshot) {
    if (row.followup_required || row.has_new_activity_since_report || row.workflow_changed_since_signoff) {
      pushUnique({
        key: 'review-new-activity',
        label: ACCEPTANCE_UI_LABELS.reviewNewActivity,
        onClick: args.onReviewFollowUp,
        tier: 'primary',
        variant: 'contained',
      });
    }

    const caseId = resolveSafetyCaseId(row);
    if (caseId) {
      pushUnique({
        key: 'open-live-case',
        label: ACCEPTANCE_UI_LABELS.openLiveSafetyCase,
        onClick: args.onOpenLiveCase,
        tier: actions.length === 0 ? 'primary' : 'secondary',
        variant: actions.length === 0 ? 'contained' : 'outlined',
      });
    }

    if (row.workflow_changed_since_signoff || row.has_new_activity_since_report) {
      pushUnique({
        key: 'create-updated-scan',
        label: ACCEPTANCE_UI_LABELS.createUpdatedSafetyScan,
        onClick: args.onCreateUpdatedScan,
        tier: actions.length === 0 ? 'primary' : 'secondary',
        variant: actions.length === 0 ? 'contained' : 'outlined',
      });
    }

    const signedReportId = resolveSignedSafetyScanReportId(row);
    if (signedReportId) {
      pushUnique({
        key: 'view-signed-scan',
        label: ACCEPTANCE_UI_LABELS.viewSignedSafetyScan,
        onClick: args.onViewSignedScan,
        tier: 'tertiary',
        variant: 'text',
      });
    }

    return actions;
  }

  const blocker = args.resolvePrimaryBlocker();
  if (blocker.code === 'evidence_review_pending') {
    pushUnique({ key: 'complete-evidence-review', label: 'Complete Evidence Review', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'representative_evidence_missing') {
    pushUnique({ key: 'review-evidence', label: 'Review Evidence', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'followup_owner_missing') {
    pushUnique({ key: 'assign-follow-up-owner', label: 'Assign Follow-up Owner', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'followup_action_incomplete') {
    pushUnique({ key: 'record-corrective-response', label: 'Record Corrective Response', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'exposure_measurement_incomplete') {
    pushUnique({ key: 'continue-monitoring', label: 'Continue Monitoring', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'package_not_generated') {
    pushUnique({ key: 'generate-evidence-package', label: 'Generate Safety Scan', onClick: args.onCreateUpdatedScan, tier: 'primary', variant: 'contained' });
  } else if (blocker.code === 'final_signoff_pending') {
    pushUnique({
      key: 'complete-final-signoff',
      label: 'Complete Final Sign-off',
      onClick: args.onOpenDraftScan,
      tier: 'primary',
      variant: 'contained',
    });
  } else if (normalizeText(row.report_status) === 'candidate') {
    if (normalizeText(row.review_status) === 'reviewed') {
      pushUnique({ key: 'create-safety-case', label: 'Create Safety Case', onClick: args.onSaveFinding, tier: 'primary', variant: 'contained' });
    } else {
      pushUnique({ key: 'review-evidence', label: 'Review Evidence', onClick: args.onOpenReview, tier: 'primary', variant: 'contained' });
    }
  } else if (resolveDraftSafetyScanReportId(row)) {
    const draftLabel = resolveDraftSafetyScanButtonLabel(row);
    pushUnique({ key: 'open-draft-scan', label: draftLabel, onClick: args.onOpenDraftScan, tier: 'primary', variant: 'contained' });
  }

  if (resolveSafetyCaseId(row) && actions[0]?.label !== ACCEPTANCE_UI_LABELS.openLiveSafetyCase) {
    pushUnique({
      key: 'open-live-case',
      label: ACCEPTANCE_UI_LABELS.openLiveSafetyCase,
      onClick: args.onOpenLiveCase,
      tier: actions.length === 0 ? 'primary' : 'secondary',
      variant: actions.length === 0 ? 'contained' : 'outlined',
    });
  }

  const signedReportId = resolveSignedSafetyScanReportId(row);
  if (signedReportId && !actions.some((action) => action.label === ACCEPTANCE_UI_LABELS.viewSignedSafetyScan)) {
    pushUnique({
      key: 'view-signed-scan',
      label: ACCEPTANCE_UI_LABELS.viewSignedSafetyScan,
      onClick: args.onViewSignedScan,
      tier: 'tertiary',
      variant: 'text',
    });
  }

  return actions;
}

export function buildFinalizedPortfolioHierarchyActions(args: {
  row: {
    finding_id?: string | null;
    safety_case_id?: string | null;
    latest_finalized_report_id?: string | null;
    report_id?: string | null;
    report_status?: string | null;
    followup_required?: boolean | null;
    has_new_activity_since_report?: boolean | null;
    workflow_changed_since_signoff?: boolean | null;
    package_available?: boolean | null;
  };
  onOpenLiveCase: () => void;
  onViewSignedScan: () => void;
  onCreateUpdatedScan: () => void;
  onReviewFollowUp: () => void;
  onDownloadPackage: () => void;
}): GroupedPortfolioAction[] {
  const actions: GroupedPortfolioAction[] = [];
  const caseId = resolveSafetyCaseId(args.row);
  const signedReportId = resolveSignedSafetyScanReportId(args.row);

  if (caseId) {
    actions.push({
      key: 'open-live-case',
      label: ACCEPTANCE_UI_LABELS.openLiveSafetyCase,
      onClick: args.onOpenLiveCase,
      group: 'liveCase',
      variant: 'contained',
    });
  }
  if (signedReportId) {
    actions.push({
      key: 'review-new-activity',
      label: ACCEPTANCE_UI_LABELS.reviewNewActivity,
      onClick: args.onReviewFollowUp,
      group: 'liveCase',
      variant: 'outlined',
    });
  }
  if (signedReportId) {
    actions.push({
      key: 'view-signed-scan',
      label: ACCEPTANCE_UI_LABELS.viewSignedSafetyScan,
      onClick: args.onViewSignedScan,
      group: 'scanExport',
      variant: 'contained',
    });
  }
  if (args.row.workflow_changed_since_signoff || args.row.has_new_activity_since_report) {
    actions.push({
      key: 'create-updated-scan',
      label: ACCEPTANCE_UI_LABELS.createUpdatedSafetyScan,
      onClick: args.onCreateUpdatedScan,
      group: 'scanExport',
      variant: 'outlined',
    });
  }
  if (args.row.package_available) {
    actions.push({
      key: 'download-package',
      label: ACCEPTANCE_UI_LABELS.downloadPackage,
      onClick: args.onDownloadPackage,
      group: 'scanExport',
      variant: 'outlined',
    });
  }
  return actions;
}
